import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  Database,
  Edit3,
  CheckCircle,
  X,
  Save,
  Loader2,
  Copy,
  Download
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DatabaseConfigEditor = ({ 
  open, 
  onOpenChange, 
  entityData = null, 
  isEditMode = false, 
  onSave = null 
}) => {
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableConfig, setEditableConfig] = useState(null);
  const [jsonEditorModal, setJsonEditorModal] = useState({ 
    open: false, 
    title: "", 
    value: null, 
    path: null, 
    table: null, 
    field: null 
  });
  const [jsonEditorView, setJsonEditorView] = useState("tree");
  const [tempJsonValue, setTempJsonValue] = useState("");
  const [sqlModal, setSqlModal] = useState({
    open: false,
    sql: "",
    tableName: "",
    recordCount: 0
  });
  const [generatingSql, setGeneratingSql] = useState(false);

  // Fetch configuration data when component opens
  useEffect(() => {
    if (open && entityData && isEditMode) {
      fetchConfigurationData();
    } else if (open && !isEditMode) {
      // For new configurations, use provided data
      setConfigData(entityData);
      setEditableConfig(JSON.parse(JSON.stringify(entityData)));
    }
  }, [open, entityData, isEditMode]);

  const fetchConfigurationData = async () => {
    if (!entityData?.source || !entityData?.target) return;
    
    setLoading(true);
    try {
      // Extract entity name from entityData - it might be in different fields
      const entityName = entityData.entity || entityData.entityName || getEntityName(entityData);
      const entityParam = entityName ? `?entity=${encodeURIComponent(entityName)}` : '';
      
      console.log('Fetching configuration for:', entityData.source, '->', entityData.target, 'Entity:', entityName);
      
      const response = await axios.get(`${API}/source-target-pair-details/${entityData.source}/${entityData.target}${entityParam}`);
      setConfigData(response.data);
      setEditableConfig(JSON.parse(JSON.stringify(response.data)));
    } catch (error) {
      console.error("Error fetching configuration data:", error);
      toast.error("Failed to load configuration data");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract entity name from various possible fields
  const getEntityName = (entity) => {
    if (entity.entity) return entity.entity;
    if (entity.entityName) return entity.entityName;
    if (entity.sourceField && typeof entity.sourceField === 'object' && entity.sourceField.entity) {
      return entity.sourceField.entity;
    }
    if (entity.targetField && typeof entity.targetField === 'object' && entity.targetField.entity) {
      return entity.targetField.entity;
    }
    return null;
  };

  // Helper function to categorize migration targets based on their relationship to the entity
  const categorizeTargets = () => {
    if (!configData.migration_targets || !configData.migration_entities || configData.migration_entities.length === 0) {
      return { mainTargets: [], dependentTargets: [], mappingTargets: [] };
    }

    const entity = configData.migration_entities[0]; // Should be only one for specific entity
    const allTargets = configData.migration_targets;
    
    const mainTargets = [];
    const dependentTargets = [];
    const mappingTargets = [];

    // Get IDs for categorization
    const mainTargetId = entity.mainTarget;
    const dependentTargetIds = Array.isArray(entity.dependentTargets) ? entity.dependentTargets : [];
    const mappingTargetIds = Array.isArray(entity.mappingTargets) ? entity.mappingTargets : [];

    allTargets.forEach(target => {
      if (target.id === mainTargetId) {
        mainTargets.push(target);
      } else if (dependentTargetIds.includes(target.id)) {
        dependentTargets.push(target);
      } else if (mappingTargetIds.includes(target.id)) {
        mappingTargets.push(target);
      }
      // Note: Some targets might not fall into any category if the relationships are complex
    });

    return { mainTargets, dependentTargets, mappingTargets };
  };

  const handleConfigEdit = (table, field, value) => {
    const newConfig = { ...editableConfig };
    
    // Handle ID-based tables (migration_targets, source_filter_options, validate_auth)
    if ((table === 'migration_targets' || table === 'source_filter_options' || table === 'validate_auth') && field.includes('_')) {
      const [recordId, actualField] = field.split('_', 2);
      if (!newConfig[table]) {
        newConfig[table] = [];
      }
      
      // Find the record by ID and update it
      const recordIndex = newConfig[table].findIndex(item => item.id === parseInt(recordId));
      if (recordIndex !== -1) {
        newConfig[table][recordIndex][actualField] = value;
      }
    } else if (field.includes('_')) {
      // Handle indexed fields like "0_source", "1_provider"
      const [index, actualField] = field.split('_', 2);
      if (!newConfig[table]) {
        newConfig[table] = [];
      }
      if (!newConfig[table][parseInt(index)]) {
        newConfig[table][parseInt(index)] = {};
      }
      newConfig[table][parseInt(index)][actualField] = value;
    } else {
      // Handle regular field editing
      if (!newConfig[table]) {
        newConfig[table] = {};
      }
      newConfig[table][field] = value;
    }
    
    setEditableConfig(newConfig);
  };

  const handleJsonEdit = (table, field, jsonPath = null) => {
    let value;
    let displayFieldName = field;
    
    if (field.includes('_')) {
      // Split field into ID/index and actual field name
      const [idOrIndex, actualField] = field.split('_', 2);
      displayFieldName = actualField;
      
      // Handle ID-based tables (migration_targets, source_filter_options, validate_auth)
      if (table === 'migration_targets' || table === 'source_filter_options' || table === 'validate_auth') {
        const record = editableConfig?.[table]?.find(item => item.id === parseInt(idOrIndex));
        value = record?.[actualField];
      } 
      // Handle index-based tables (migration_entities, migration_sources, etc.)
      else if (editableConfig?.[table] && Array.isArray(editableConfig[table])) {
        value = editableConfig[table][parseInt(idOrIndex)]?.[actualField];
      } else {
        value = editableConfig?.[table]?.[field];
      }
    } else {
      value = editableConfig?.[table]?.[field];
    }

    console.log('Opening JSON editor for:', {table, field, value});

    setJsonEditorModal({
      open: true,
      title: displayFieldName,
      value: value,
      path: jsonPath,
      table: table,
      field: field
    });
    
    // Properly format the JSON value for editing
    let jsonString = '';
    if (value === null || value === undefined) {
      jsonString = 'null';
    } else if (typeof value === 'object') {
      jsonString = JSON.stringify(value, null, 2);
    } else if (typeof value === 'string') {
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(value);
        jsonString = JSON.stringify(parsed, null, 2);
      } catch (e) {
        // If it fails to parse as JSON, treat it as a plain string
        // Only wrap in quotes if it's not already JSON-formatted
        jsonString = value;
      }
    } else {
      jsonString = JSON.stringify(value, null, 2);
    }
    
    setTempJsonValue(jsonString);
  };

  const handleJsonSave = () => {
    try {
      // Try to parse the JSON value
      let parsed;
      try {
        parsed = JSON.parse(tempJsonValue);
      } catch (parseError) {
        // If parse fails, check if it's already valid JSON as a trimmed string
        const trimmed = tempJsonValue.trim();
        if (trimmed === 'null' || trimmed === 'undefined' || trimmed === '') {
          parsed = null;
        } else {
          // Re-throw the original error with more context
          throw new Error(`JSON syntax error at position ${parseError.message}`);
        }
      }
      
      const newConfig = { ...editableConfig };
      
      if (jsonEditorModal.field.includes('_')) {
        const [idOrIndex, actualField] = jsonEditorModal.field.split('_', 2);
        
        // Handle ID-based tables (migration_targets, source_filter_options, validate_auth)
        if (jsonEditorModal.table === 'migration_targets' || 
            jsonEditorModal.table === 'source_filter_options' || 
            jsonEditorModal.table === 'validate_auth') {
          if (!newConfig[jsonEditorModal.table]) {
            newConfig[jsonEditorModal.table] = [];
          }
          
          // Find the record by ID and update it
          const recordIndex = newConfig[jsonEditorModal.table].findIndex(item => item.id === parseInt(idOrIndex));
          if (recordIndex !== -1) {
            newConfig[jsonEditorModal.table][recordIndex][actualField] = parsed;
          }
        } 
        // Handle index-based tables (migration_entities, migration_sources, etc.)
        else {
          if (!newConfig[jsonEditorModal.table]) {
            newConfig[jsonEditorModal.table] = [];
          }
          if (!newConfig[jsonEditorModal.table][parseInt(idOrIndex)]) {
            newConfig[jsonEditorModal.table][parseInt(idOrIndex)] = {};
          }
          newConfig[jsonEditorModal.table][parseInt(idOrIndex)][actualField] = parsed;
        }
      } else {
        if (!newConfig[jsonEditorModal.table]) {
          newConfig[jsonEditorModal.table] = {};
        }
        newConfig[jsonEditorModal.table][jsonEditorModal.field] = parsed;
      }
      
      setEditableConfig(newConfig);
      setJsonEditorModal({ open: false, title: "", value: null, path: null, table: null, field: null });
      toast.success("JSON updated successfully");
    } catch (error) {
      console.error('JSON parse error:', error);
      toast.error("Invalid JSON: " + error.message);
    }
  };

  const toggleEditing = () => {
    if (isEditing) {
      // Reset to original config
      setEditableConfig(JSON.parse(JSON.stringify(configData)));
    }
    setIsEditing(!isEditing);
  };

  const saveChanges = async () => {
    if (onSave) {
      try {
        await onSave(editableConfig);
        setConfigData(JSON.parse(JSON.stringify(editableConfig)));
        setIsEditing(false);
        toast.success("Configuration saved successfully");
      } catch (error) {
        console.error("Error saving configuration:", error);
        toast.error("Failed to save configuration");
      }
    } else {
      setConfigData(JSON.parse(JSON.stringify(editableConfig)));
      setIsEditing(false);
      toast.success("Configuration updated");
    }
  };

  const discardChanges = () => {
    setEditableConfig(JSON.parse(JSON.stringify(configData)));
    setIsEditing(false);
  };

  const handleGenerateSQL = async (tableName) => {
    setGeneratingSql(true);
    try {
      const records = configData[tableName] || [];
      
      if (records.length === 0) {
        toast.error(`No records found in ${tableName}`);
        return;
      }

      const response = await axios.post(`${API}/generate-insert-sql`, {
        table: tableName,
        records: records
      });

      setSqlModal({
        open: true,
        sql: response.data.sql,
        tableName: tableName,
        recordCount: response.data.record_count
      });

      toast.success(`Generated SQL for ${response.data.record_count} record(s)`);
    } catch (error) {
      console.error("Error generating SQL:", error);
      toast.error(error.response?.data?.detail || "Failed to generate SQL");
    } finally {
      setGeneratingSql(false);
    }
  };

  const copySqlToClipboard = () => {
    navigator.clipboard.writeText(sqlModal.sql);
    toast.success("SQL copied to clipboard!");
  };

  const downloadSql = () => {
    const blob = new Blob([sqlModal.sql], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sqlModal.tableName}_insert.sql`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("SQL file downloaded!");
  };


  const displayJson = (value) => {
    if (!value) return 'null';
    
    // If it's already an object/array, stringify it
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    
    // If it's a string, try to parse it first to see if it's JSON
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        // If successful, it was a JSON string, so display it formatted
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        // Not JSON, just return the string as-is
        return value;
      }
    }
    
    // For other types (numbers, booleans), convert to string
    return String(value);
  };

  const renderStringField = (table, field, label, value) => (
    <div key={field}>
      <Label className="text-xs font-semibold text-gray-700">{label}</Label>
      {isEditing ? (
        <Input 
          value={value || ''} 
          onChange={(e) => handleConfigEdit(table, field, e.target.value)}
          className="mt-1 text-xs h-8"
        />
      ) : (
        <div className="mt-1 p-2 border rounded bg-gray-50 text-xs min-h-[32px]">
          {value || 'Not set'}
        </div>
      )}
    </div>
  );

  const renderJsonField = (table, field, label, value) => (
    <div key={field} className="col-span-2">
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs font-semibold text-gray-700">{label}</Label>
        <Button
          onClick={() => handleJsonEdit(table, field)}
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs hover:bg-blue-50"
        >
          <Edit3 className="w-3 h-3 mr-1" />
          Edit
        </Button>
      </div>
      <div className="p-2 border rounded bg-gray-50 text-xs max-h-32 overflow-y-auto">
        <pre className="whitespace-pre-wrap font-mono">
          {displayJson(value)}
        </pre>
      </div>
    </div>
  );

  // Helper function to render a section of targets
  const renderTargetSection = (targets, sectionTitle, sectionIcon, sectionDescription) => {
    if (targets.length === 0) {
      return (
        <div className="mb-6">
          <h5 className="font-medium text-gray-700 mb-4 flex items-center">
            <span className="mr-2">{sectionIcon}</span>
            {sectionTitle}
            <span className="ml-2 text-xs text-gray-500">({sectionDescription})</span>
          </h5>
          <div className="text-center text-gray-500 py-4 bg-gray-100 rounded border-2 border-dashed">
            No {sectionTitle.toLowerCase()} found
          </div>
        </div>
      );
    }

    return (
      <div className="mb-6">
        <h5 className="font-medium text-gray-700 mb-4 flex items-center">
          <span className="mr-2">{sectionIcon}</span>
          {sectionTitle}
          <span className="ml-2 text-xs text-gray-500">({sectionDescription})</span>
          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{targets.length} record{targets.length > 1 ? 's' : ''}</span>
        </h5>
        {targets.map((target, index) => (
          <div key={target.id} className="mb-4 p-4 border rounded bg-gray-50">
            <h6 className="font-medium text-gray-600 mb-3">
              {target.name || `Target #${index + 1}`} 
              <span className="ml-2 text-xs text-gray-500">ID: {target.id}</span>
            </h6>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {/* String Fields */}
              {renderStringField('migration_targets', `${target.id}_name`, 'Name', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.name : target.name)}
              {renderStringField('migration_targets', `${target.id}_operation`, 'Operation', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.operation : target.operation)}
              {renderStringField('migration_targets', `${target.id}_source`, 'Source', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.source : target.source)}
              {renderStringField('migration_targets', `${target.id}_migrationEntity`, 'Migration Entity', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.migrationEntity : target.migrationEntity)}
              {renderStringField('migration_targets', `${target.id}_condition`, 'Condition', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.condition : target.condition)}
              {renderStringField('migration_targets', `${target.id}_target_type`, 'Target Type', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.target_type : target.target_type)}
              {renderStringField('migration_targets', `${target.id}_target_response_type`, 'Target Response Type', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.target_response_type : target.target_response_type)}
              {renderStringField('migration_targets', `${target.id}_isMappingTarget`, 'Is Mapping Target', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.isMappingTarget : target.isMappingTarget)}
              {renderStringField('migration_targets', `${target.id}_isMainTarget`, 'Is Main Target', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.isMainTarget : target.isMainTarget)}
              {renderStringField('migration_targets', `${target.id}_targetUniqueIdentifier`, 'Target Unique Identifier', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.targetUniqueIdentifier : target.targetUniqueIdentifier)}
              {renderStringField('migration_targets', `${target.id}_sourceKeyForChild`, 'Source Key For Child', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.sourceKeyForChild : target.sourceKeyForChild)}
              {renderStringField('migration_targets', `${target.id}_mappingDisplayField`, 'Mapping Display Field', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.mappingDisplayField : target.mappingDisplayField)}
              
              {/* JSON Fields */}
              {renderJsonField('migration_targets', `${target.id}_targetExecutor`, 'Target Executor', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.targetExecutor : target.targetExecutor)}
              {renderJsonField('migration_targets', `${target.id}_targetProvider`, 'Target Provider', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.targetProvider : target.targetProvider)}
              {renderJsonField('migration_targets', `${target.id}_targetFields`, 'Target Fields', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.targetFields : target.targetFields)}
              {renderJsonField('migration_targets', `${target.id}_fieldMappings`, 'Field Mappings', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.fieldMappings : target.fieldMappings)}
              {renderJsonField('migration_targets', `${target.id}_additionalDetails`, 'Additional Details', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.additionalDetails : target.additionalDetails)}
              {renderJsonField('migration_targets', `${target.id}_sourceValidationKeys`, 'Source Validation Keys', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.sourceValidationKeys : target.sourceValidationKeys)}
              {renderJsonField('migration_targets', `${target.id}_sourceDataValidationKeys`, 'Source Data Validation Keys', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.sourceDataValidationKeys : target.sourceDataValidationKeys)}
              {renderJsonField('migration_targets', `${target.id}_overrideFields`, 'Override Fields', 
                isEditing ? editableConfig.migration_targets.find(t => t.id === target.id)?.overrideFields : target.overrideFields)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] bg-white text-slate-900">
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-slate-700">Loading configuration...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!configData) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden bg-white text-slate-900 flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center space-x-2 text-slate-900">
              <Database className="w-5 h-5" />
              <span>Database Configuration Editor</span>
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {isEditMode ? "Edit existing configuration" : "View and modify generated configuration"}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-medium">Database Configuration</h3>
              </div>
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button 
                      onClick={saveChanges}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </Button>
                    <Button 
                      onClick={discardChanges}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={toggleEditing}
                    variant="outline" 
                    size="sm"
                  >
                    <Edit3 className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                )}
              </div>
            </div>

            {/* Tabs Navigation */}
            <Tabs defaultValue="entities" className="w-full">
              <TabsList className="grid w-full grid-cols-7 mb-4">
                <TabsTrigger value="entities">Migration Entities</TabsTrigger>
                <TabsTrigger value="sources">Migration Sources</TabsTrigger>
                <TabsTrigger value="targets">Migration Targets</TabsTrigger>
                <TabsTrigger value="connection">Connection Fields</TabsTrigger>
                <TabsTrigger value="validate">Validate Data</TabsTrigger>
                <TabsTrigger value="filter">Filter Options</TabsTrigger>
                <TabsTrigger value="auth">Validate Auth</TabsTrigger>
              </TabsList>
              
              <TabsContent value="entities" className="space-y-4">
                {/* MIGRATION_ENTITIES Table */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-base text-gray-800">🔗 Migration Entities</h4>
                    <Button
                      onClick={() => handleGenerateSQL('migration_entities')}
                      disabled={generatingSql || !configData.migration_entities || configData.migration_entities.length === 0}
                      size="sm"
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {generatingSql ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Database className="w-4 h-4 mr-2" /> Generate INSERT SQL</>
                      )}
                    </Button>
                  </div>
                  <div className="border-b mb-4"></div>
                  
                  {configData.migration_entities && configData.migration_entities.length > 0 ? (
                    configData.migration_entities.map((entity, index) => (
                      <div key={index} className="mb-6 p-4 border rounded bg-gray-50">
                        <h5 className="font-medium text-gray-700 mb-4">Entity #{index + 1} - {entity.entity || 'Unknown'}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          {/* String Fields */}
                          {renderStringField('migration_entities', `${index}_source`, 'Source', 
                            isEditing ? editableConfig.migration_entities[index]?.source : entity.source)}
                          {renderStringField('migration_entities', `${index}_target`, 'Target', 
                            isEditing ? editableConfig.migration_entities[index]?.target : entity.target)}
                          {renderStringField('migration_entities', `${index}_entity`, 'Entity', 
                            isEditing ? editableConfig.migration_entities[index]?.entity : entity.entity)}
                          {renderStringField('migration_entities', `${index}_customFieldKey`, 'Custom Field Key', 
                            isEditing ? editableConfig.migration_entities[index]?.customFieldKey : entity.customFieldKey)}
                          {renderStringField('migration_entities', `${index}_delimiter`, 'Delimiter', 
                            isEditing ? editableConfig.migration_entities[index]?.delimiter : entity.delimiter)}
                          {renderStringField('migration_entities', `${index}_migrationObject`, 'Migration Object', 
                            isEditing ? editableConfig.migration_entities[index]?.migrationObject : entity.migrationObject)}
                          {renderStringField('migration_entities', `${index}_mainTarget`, 'Main Target', 
                            isEditing ? editableConfig.migration_entities[index]?.mainTarget : entity.mainTarget)}
                          {renderStringField('migration_entities', `${index}_isDelimitteEnabled`, 'Is Delimiter Enabled', 
                            isEditing ? editableConfig.migration_entities[index]?.isDelimitteEnabled : entity.isDelimitteEnabled)}
                          {renderStringField('migration_entities', `${index}_selectMultipleEntities`, 'Select Multiple Entities', 
                            isEditing ? editableConfig.migration_entities[index]?.selectMultipleEntities : entity.selectMultipleEntities)}
                          
                          {/* JSON Fields */}
                          {renderJsonField('migration_entities', `${index}_sourceExtender`, 'Source Extender', 
                            isEditing ? editableConfig.migration_entities[index]?.sourceExtender : entity.sourceExtender)}
                          {renderJsonField('migration_entities', `${index}_sourceField`, 'Source Field', 
                            isEditing ? editableConfig.migration_entities[index]?.sourceField : entity.sourceField)}
                          {renderJsonField('migration_entities', `${index}_targetField`, 'Target Field', 
                            isEditing ? editableConfig.migration_entities[index]?.targetField : entity.targetField)}
                          {renderJsonField('migration_entities', `${index}_dependentTargets`, 'Dependent Targets', 
                            isEditing ? editableConfig.migration_entities[index]?.dependentTargets : entity.dependentTargets)}
                          {renderJsonField('migration_entities', `${index}_mappingTargets`, 'Mapping Targets', 
                            isEditing ? editableConfig.migration_entities[index]?.mappingTargets : entity.mappingTargets)}
                          {renderJsonField('migration_entities', `${index}_incompatible`, 'Incompatible', 
                            isEditing ? editableConfig.migration_entities[index]?.incompatible : entity.incompatible)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">No migration entities found</div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="sources" className="space-y-4">
                {/* MIGRATION_SOURCES Table */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-base text-gray-800">📤 Migration Sources</h4>
                    <Button
                      onClick={() => handleGenerateSQL('migration_sources')}
                      disabled={generatingSql || !configData.migration_sources || configData.migration_sources.length === 0}
                      size="sm"
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {generatingSql ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Database className="w-4 h-4 mr-2" /> Generate INSERT SQL</>
                      )}
                    </Button>
                  </div>
                  <div className="border-b mb-4"></div>
                  
                  {configData.migration_sources && configData.migration_sources.length > 0 ? (
                    configData.migration_sources.map((source, index) => (
                      <div key={index} className="mb-6 p-4 border rounded bg-gray-50">
                        <h5 className="font-medium text-gray-700 mb-4">Source #{index + 1} - {source.source || 'Unknown'}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          {/* String Fields */}
                          {renderStringField('migration_sources', `${index}_source`, 'Source', 
                            isEditing ? editableConfig.migration_sources[index]?.source : source.source)}
                          {renderStringField('migration_sources', `${index}_provider`, 'Provider', 
                            isEditing ? editableConfig.migration_sources[index]?.provider : source.provider)}
                          {renderStringField('migration_sources', `${index}_migrationEntity`, 'Migration Entity', 
                            isEditing ? editableConfig.migration_sources[index]?.migrationEntity : source.migrationEntity)}
                          {renderStringField('migration_sources', `${index}_batchSize`, 'Batch Size', 
                            isEditing ? editableConfig.migration_sources[index]?.batchSize : source.batchSize)}
                          {renderStringField('migration_sources', `${index}_sourceUniqueIdentifier`, 'Source Unique Identifier', 
                            isEditing ? editableConfig.migration_sources[index]?.sourceUniqueIdentifier : source.sourceUniqueIdentifier)}
                          {renderStringField('migration_sources', `${index}_sourceAuthType`, 'Source Auth Type', 
                            isEditing ? editableConfig.migration_sources[index]?.sourceAuthType : source.sourceAuthType)}
                          {renderStringField('migration_sources', `${index}_paginationMethod`, 'Pagination Method', 
                            isEditing ? editableConfig.migration_sources[index]?.paginationMethod : source.paginationMethod)}
                          {renderStringField('migration_sources', `${index}_source_rootMapping`, 'Source Root Mapping', 
                            isEditing ? editableConfig.migration_sources[index]?.source_rootMapping : source.source_rootMapping)}
                          {renderStringField('migration_sources', `${index}_source_response_type`, 'Source Response Type', 
                            isEditing ? editableConfig.migration_sources[index]?.source_response_type : source.source_response_type)}
                          
                          {/* JSON Fields */}
                          {renderJsonField('migration_sources', `${index}_sourceExecutor`, 'Source Executor', 
                            isEditing ? editableConfig.migration_sources[index]?.sourceExecutor : source.sourceExecutor)}
                          {renderJsonField('migration_sources', `${index}_sourceProvider`, 'Source Provider', 
                            isEditing ? editableConfig.migration_sources[index]?.sourceProvider : source.sourceProvider)}
                          {renderJsonField('migration_sources', `${index}_paginationFields`, 'Pagination Fields', 
                            isEditing ? editableConfig.migration_sources[index]?.paginationFields : source.paginationFields)}
                          {renderJsonField('migration_sources', `${index}_distinct_fields`, 'Distinct Fields', 
                            isEditing ? editableConfig.migration_sources[index]?.distinct_fields : source.distinct_fields)}
                          {renderJsonField('migration_sources', `${index}_sourceMappingExecutor`, 'Source Mapping Executor', 
                            isEditing ? editableConfig.migration_sources[index]?.sourceMappingExecutor : source.sourceMappingExecutor)}
                          {renderJsonField('migration_sources', `${index}_sourceQueryParamsOptions`, 'Source Query Params Options', 
                            isEditing ? editableConfig.migration_sources[index]?.sourceQueryParamsOptions : source.sourceQueryParamsOptions)}
                          {renderJsonField('migration_sources', `${index}_sourceResponseAttributesExecutor`, 'Source Response Attributes Executor', 
                            isEditing ? editableConfig.migration_sources[index]?.sourceResponseAttributesExecutor : source.sourceResponseAttributesExecutor)}
                          {renderJsonField('migration_sources', `${index}_targetMappingExecutor`, 'Target Mapping Executor', 
                            isEditing ? editableConfig.migration_sources[index]?.targetMappingExecutor : source.targetMappingExecutor)}
                          {renderJsonField('migration_sources', `${index}_additionalDetails`, 'Additional Details', 
                            isEditing ? editableConfig.migration_sources[index]?.additionalDetails : source.additionalDetails)}
                          {renderJsonField('migration_sources', `${index}_sourceMappingConfig`, 'Source Mapping Config', 
                            isEditing ? editableConfig.migration_sources[index]?.sourceMappingConfig : source.sourceMappingConfig)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">No migration sources found</div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="targets" className="space-y-4">
                {/* MIGRATION_TARGETS Table with Sections */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-base text-gray-800">📥 Migration Targets</h4>
                    <Button
                      onClick={() => handleGenerateSQL('migration_targets')}
                      disabled={generatingSql || !configData.migration_targets || configData.migration_targets.length === 0}
                      size="sm"
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {generatingSql ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Database className="w-4 h-4 mr-2" /> Generate INSERT SQL</>
                      )}
                    </Button>
                  </div>
                  <div className="border-b mb-6"></div>
                  
                  {(() => {
                    const { mainTargets, dependentTargets, mappingTargets } = categorizeTargets();
                    
                    return (
                      <div className="space-y-6">
                        {/* Main Target Section */}
                        {renderTargetSection(
                          mainTargets, 
                          "Main Target", 
                          "🎯", 
                          "Primary target for data migration"
                        )}
                        
                        {/* Dependent Targets Section */}
                        {renderTargetSection(
                          dependentTargets, 
                          "Dependent Targets", 
                          "🔗", 
                          "Secondary targets that depend on main target"
                        )}
                        
                        {/* Mapping Targets Section */}
                        {renderTargetSection(
                          mappingTargets, 
                          "Mapping Targets", 
                          "🗺️", 
                          "Targets used for field mapping configuration"
                        )}
                        
                        {/* Show message if no targets found at all */}
                        {mainTargets.length === 0 && dependentTargets.length === 0 && mappingTargets.length === 0 && (
                          <div className="text-center text-gray-500 py-8">
                            <Database className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                            <p>No migration targets found for this entity</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </TabsContent>

              <TabsContent value="connection" className="space-y-4">
                {/* TARGET_CONNECTION_FIELDS Table */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-base text-gray-800">🔌 Connection Fields</h4>
                    <Button
                      onClick={() => handleGenerateSQL('target_connection_fields')}
                      disabled={generatingSql || !configData.target_connection_fields || configData.target_connection_fields.length === 0}
                      size="sm"
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {generatingSql ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Database className="w-4 h-4 mr-2" /> Generate INSERT SQL</>
                      )}
                    </Button>
                  </div>
                  <div className="border-b mb-4"></div>
                  
                  {configData.target_connection_fields && configData.target_connection_fields.length > 0 ? (
                    configData.target_connection_fields.map((field, index) => (
                      <div key={index} className="mb-6 p-4 border rounded bg-gray-50">
                        <h5 className="font-medium text-gray-700 mb-4">Connection #{index + 1} - {field.targetEntity || 'Unknown'}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          {/* String Fields */}
                          {renderStringField('target_connection_fields', `${index}_source`, 'Source', 
                            isEditing ? editableConfig.target_connection_fields[index]?.source : field.source)}
                          {renderStringField('target_connection_fields', `${index}_target`, 'Target', 
                            isEditing ? editableConfig.target_connection_fields[index]?.target : field.target)}
                          {renderStringField('target_connection_fields', `${index}_authType`, 'Auth Type', 
                            isEditing ? editableConfig.target_connection_fields[index]?.authType : field.authType)}
                          {renderStringField('target_connection_fields', `${index}_domainType`, 'Domain Type', 
                            isEditing ? editableConfig.target_connection_fields[index]?.domainType : field.domainType)}
                          
                          {/* JSON Fields */}
                          {renderJsonField('target_connection_fields', `${index}_fields`, 'Fields', 
                            isEditing ? editableConfig.target_connection_fields[index]?.fields : field.fields)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">No connection fields found</div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="validate" className="space-y-4">
                {/* VALIDATE_SOURCE_DATA Table */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-base text-gray-800">✅ Validate Source Data</h4>
                    <Button
                      onClick={() => handleGenerateSQL('validate_source_data')}
                      disabled={generatingSql || !configData.validate_source_data || configData.validate_source_data.length === 0}
                      size="sm"
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {generatingSql ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Database className="w-4 h-4 mr-2" /> Generate INSERT SQL</>
                      )}
                    </Button>
                  </div>
                  <div className="border-b mb-4"></div>
                  
                  {configData.validate_source_data && configData.validate_source_data.length > 0 ? (
                    configData.validate_source_data.map((validate, index) => (
                      <div key={index} className="mb-6 p-4 border rounded bg-gray-50">
                        <h5 className="font-medium text-gray-700 mb-4">Validation #{index + 1} - {validate.entity || 'Unknown'}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          {/* String Fields */}
                          {renderStringField('validate_source_data', `${index}_source`, 'Source', 
                            isEditing ? editableConfig.validate_source_data[index]?.source : validate.source)}
                          {renderStringField('validate_source_data', `${index}_target`, 'Target', 
                            isEditing ? editableConfig.validate_source_data[index]?.target : validate.target)}
                          {renderStringField('validate_source_data', `${index}_entity`, 'Entity', 
                            isEditing ? editableConfig.validate_source_data[index]?.entity : validate.entity)}
                          {renderStringField('validate_source_data', `${index}_source_response_type`, 'Source Response Type', 
                            isEditing ? editableConfig.validate_source_data[index]?.source_response_type : validate.source_response_type)}
                          
                          {/* JSON Fields */}
                          {renderJsonField('validate_source_data', `${index}_sourceExecutor`, 'Source Executor', 
                            isEditing ? editableConfig.validate_source_data[index]?.sourceExecutor : validate.sourceExecutor)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">No validation data found</div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="filter" className="space-y-4">
                {/* SOURCE_FILTER_OPTIONS Table */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-base text-gray-800">⚙️ Source Filter Options</h4>
                    <Button
                      onClick={() => handleGenerateSQL('source_filter_options')}
                      disabled={generatingSql || !configData.source_filter_options || configData.source_filter_options.length === 0}
                      size="sm"
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {generatingSql ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Database className="w-4 h-4 mr-2" /> Generate INSERT SQL</>
                      )}
                    </Button>
                  </div>
                  <div className="border-b mb-4"></div>
                  
                  {configData.source_filter_options && configData.source_filter_options.length > 0 ? (
                    configData.source_filter_options.map((filter, index) => (
                      <div key={filter.id || index} className="mb-6 p-4 border rounded bg-gray-50">
                        <h5 className="font-medium text-gray-700 mb-4">Filter Option #{index + 1} - {filter.name || 'Unknown'}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          {/* String Fields */}
                          {renderStringField('source_filter_options', `${filter.id}_name`, 'Name', 
                            isEditing ? editableConfig.source_filter_options?.find(f => f.id === filter.id)?.name : filter.name)}
                          
                          {/* JSON Fields */}
                          {renderJsonField('source_filter_options', `${filter.id}_queryParams`, 'Query Params', 
                            isEditing ? editableConfig.source_filter_options?.find(f => f.id === filter.id)?.queryParams : filter.queryParams)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <p>No filter options found for source "{configData.source}"</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="auth" className="space-y-4">
                {/* VALIDATE_AUTH Table */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-base text-gray-800">🔐 Validate Auth</h4>
                    <Button
                      onClick={() => handleGenerateSQL('validate_auth')}
                      disabled={generatingSql || !configData.validate_auth || configData.validate_auth.length === 0}
                      size="sm"
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {generatingSql ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><Database className="w-4 h-4 mr-2" /> Generate INSERT SQL</>
                      )}
                    </Button>
                  </div>
                  <div className="border-b mb-4"></div>
                  
                  {configData.validate_auth && configData.validate_auth.length > 0 ? (
                    configData.validate_auth.map((auth, index) => (
                      <div key={auth.id || index} className="mb-6 p-4 border rounded bg-gray-50">
                        <h5 className="font-medium text-gray-700 mb-4">Auth Validation #{index + 1} - {auth.vendorId || 'Unknown'}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          {/* String Fields */}
                          {renderStringField('validate_auth', `${auth.id}_vendorId`, 'Vendor ID', 
                            isEditing ? editableConfig.validate_auth?.find(a => a.id === auth.id)?.vendorId : auth.vendorId)}
                          {renderStringField('validate_auth', `${auth.id}_authType`, 'Auth Type', 
                            isEditing ? editableConfig.validate_auth?.find(a => a.id === auth.id)?.authType : auth.authType)}
                          
                          {/* JSON Fields */}
                          {renderJsonField('validate_auth', `${auth.id}_apimodel`, 'API Model', 
                            isEditing ? editableConfig.validate_auth?.find(a => a.id === auth.id)?.apimodel : auth.apimodel)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <p>No auth validation found for source "{configData.source}" or target "{configData.target}"</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Fixed Footer */}
          <div className="flex justify-end pt-3 border-t border-slate-200 bg-white flex-shrink-0">
            <Button onClick={() => onOpenChange(false)} variant="outline" className="text-slate-700 border-slate-300 bg-white hover:bg-slate-50">
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* JSON Editor Modal */}
      <Dialog open={jsonEditorModal.open} onOpenChange={(open) => setJsonEditorModal({...jsonEditorModal, open})}>
        <DialogContent className="max-w-4xl max-h-[80vh] bg-white text-slate-900">
          <DialogHeader>
            <DialogTitle>Edit {jsonEditorModal.title}</DialogTitle>
            <DialogDescription>
              Modify the JSON content for this field. The JSON will be validated when you save.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="space-y-4">
              <Textarea
                value={tempJsonValue}
                onChange={(e) => setTempJsonValue(e.target.value)}
                className="font-mono text-sm min-h-[400px]"
                placeholder="Enter valid JSON..."
              />
              <div className="text-xs text-gray-500">
                💡 Tip: Make sure your JSON is properly formatted with correct brackets, quotes, and commas.
              </div>
            </div>
          </div>
          
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-xs text-gray-500">
              {(() => {
                try {
                  JSON.parse(tempJsonValue);
                  return <span className="text-green-600 flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Valid JSON</span>;
                } catch (e) {
                  return <span className="text-red-600">⚠️ Invalid JSON - please check syntax</span>;
                }
              })()}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setJsonEditorModal({...jsonEditorModal, open: false})}>
                Cancel
              </Button>
              <Button onClick={handleJsonSave} className="bg-green-600 hover:bg-green-700 text-white">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SQL Generation Modal */}
      <Dialog open={sqlModal.open} onOpenChange={(open) => setSqlModal({...sqlModal, open})}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col bg-white">
          <DialogHeader className="bg-white">
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <Database className="w-5 h-5" />
              Generated INSERT SQL - {sqlModal.tableName} ({sqlModal.recordCount} record{sqlModal.recordCount !== 1 ? 's' : ''})
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 flex flex-col space-y-4 overflow-hidden bg-white">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> This SQL is generated from your DEV database configuration. 
                Review foreign key references (marked with comments) before executing in PROD.
              </p>
            </div>
            
            <div className="flex-1 overflow-auto border border-gray-300 rounded-lg">
              <pre className="bg-white text-gray-900 p-4 text-sm font-mono whitespace-pre-wrap break-words">
                {sqlModal.sql}
              </pre>
            </div>
            
            <div className="flex gap-2 justify-end pt-2 border-t bg-white">
              <Button 
                variant="outline" 
                onClick={() => setSqlModal({...sqlModal, open: false})}
                className="bg-white text-gray-900 border-gray-300 hover:bg-gray-50"
              >
                Close
              </Button>
              <Button 
                onClick={copySqlToClipboard}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy to Clipboard
              </Button>
              <Button 
                onClick={downloadSql}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download SQL File
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default DatabaseConfigEditor;